import React, { type FC } from 'react'
import LanguageStrings from '../../../../i18n/locales'
import {
  Table,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Checkbox,
  TableBody
} from '@mui/material'
import { connect, type ConnectedProps, useSelector } from 'react-redux'
import EmptyTable from './EmptyTable'
import { alarmMetaData } from '../../redux/actionCreators'
import { type AlarmMetaDataState } from '../../redux/alarmMetaDataSlice'
import { type RootState } from '../../../../store'
import ConfigureDevice from '../configureDevice'
import { GenXDropdown, type Option } from '../../../../components/GenXDropdown'

const AddDeviceStrings =
  LanguageStrings().DeviceManagementCollectionStrings.AddDeviceComponent

/**
 * Helper function to convert frequency options to display value options
 * @param frequencyOptions - API frequency options array
 * @returns Array of options formatted for GenXDropdown component
 */
const convertMinutesToDisplayValueOption = (frequencyOptions: Array<{ label: string, value: string }>): Option[] => {
  return frequencyOptions.map(option => ({
    label: option.label,
    value: option.value
  }))
}

// Helper function to find the correct option for a given minutes value
const findExecutionFrequencyOption = (minutes: number | null, isTotalizer: boolean | undefined, frequencyOptions: Option[]): Option => {
  // For totalizers, always return the first option (typically Default)
  if (Boolean(isTotalizer) && frequencyOptions.length > 0) {
    return frequencyOptions[0]
  }

  if (minutes === null || minutes === undefined) {
    return frequencyOptions.length > 0 ? frequencyOptions[0] : { label: 'Default (1 Minute)', value: 'Default (1 Minute)' }
  }

  // Try to find matching option based on the minutes value
  const matchingOption = frequencyOptions.find(option => {
    const valueStr = option.value.toLowerCase()
    if (minutes === 1 && (valueStr.includes('1 minute') || valueStr.includes('default'))) return true
    if (minutes === 3 && valueStr.includes('3 minute')) return true
    if (minutes === 5 && valueStr.includes('5 minute')) return true
    return false
  })

  return matchingOption ?? (frequencyOptions.length > 0 ? frequencyOptions[0] : { label: 'Default (1 Minute)', value: 'Default (1 Minute)' })
}

// Helper function to convert display value to minutes
const convertDisplayValueToMinutes = (displayValue: string): number => {
  switch (displayValue) {
    case 'Default (1 Minute)':
      return 1
    case '3 Minutes':
      return 3
    case '5 Minutes':
      return 5
    default:
      return 1
  }
}

const styles = {
  selectAllCheckbox: {
    color: '#909090'
  },
  checkbox: {
    color: '#909090'
  },
  table: {
    backgroundColor: '#272727',
    borderBottom: 'none',
    boxShadow: 'none !important'
  },
  tableHead: {
    backgroundColor: '#303030',
    border: '1px solid #101010',
    boxShadow: '1px 1px 1px 0px rgba(0, 0, 0, 0.15)',
    borderRadius: '10px 10px 0px 0px'
  },
  tableHeadCell: {
    backgroundColor: '#303030',
    borderBottom: 'none',
    padding: '5px'
  },
  tableCell: {
    backgroundColor: '#272727',
    borderBottom: 'none',
    padding: '5px'
  },
  tableHeaderTypography: {
    color: '#F0F0F0',
    fontFeatureSettings: "'clig' off, 'liga' off",
    fontFamily: 'Honeywell Sans Web',
    fontSize: '16px',
    fontStyle: 'normal',
    fontWeight: 800,
    lineHeight: '24px'
  },
  tableCellTypography: {
    color: '#F0F0F0',
    fontFeatureSettings: "'clig' off, 'liga' off",
    fontFamily: 'Honeywell Sans Web',
    fontSize: '16px',
    fontStyle: 'normal',
    fontWeight: 500,
    lineHeight: '24px'
  },
  tableContainer: {
    height: '375px',
    maxHeight: '430px',
    overflow: 'auto'
  }
}

interface Indicator {
  id: string
  kpiDefinitionId: string
  kpiName: string
  assetId: string | null
  isKpiEnabled: boolean
  isAlarmApplicable: boolean
  isAlarmEnabled: boolean
  alarmHigherLimitSeverity: string | null
  alarmLowerLimitSeverity: string | null
  isCustomKpi: boolean
  tenantId: string | null
  unit: string | null
  isDefaultConfig: boolean
  executionFrequencyMinutes: number | null
  alarmLowerLimit: number | null
  alarmLowerLimitStateText: string | null
  alarmLowerLimitTextColorId: string | null
  alarmLowerLimitColorName: string | null
  alarmLowerLimitColorValue: string | null
  alarmLowerLimitSeverityId: string | null
  alarmLowerLimitSeverityLevel: string | null
  alarmLowerLimitLabelId: string | null
  alarmLowerLimitLabelName: string | null
  alarmLowerLimitRecommendationText: string
  alarmHigherLimit: string | null
  alarmHigherLimitStateText: string
  alarmHigherLimitTextColorId: string | null
  alarmHigherLimitColorName: string | null
  alarmHigherLimitColorValue: string | null
  alarmHigherLimitSeverityId: string | null
  alarmHigherLimitSeverityLevel: string | null
  alarmHigherLimitLabelId: string | null
  alarmHigherLimitLabelName: string | null
  alarmHigherLimitRecommendationText: string
  isTotalizer?: boolean
}

interface PerformanceIndicatorsProps {
  tableHeaders: string[]
  indicators: Indicator[]
  instanceType: 'standard' | 'custom'
  selectedCount: number
  toggleSelectAll: () => void
  toggleSelectIndicator: (id: string) => void
  handleChangeTextBox: (
    id: string | number,
    propertyName: string,
    value: string | number
  ) => void
  handleAlarmSelect: (
    id: string | number,
    propertyName: string,
    value: string
  ) => void
  handleCancel: (id: string, isCustomKpiFlag: boolean) => void
}

const PerformanceIndicatorsTable: FC<
PerformanceIndicatorsProps & PropsFromRedux
> = ({
  tableHeaders,
  indicators,
  instanceType,
  selectedCount,
  handleAlarmSelect,
  toggleSelectAll,
  toggleSelectIndicator,
  handleChangeTextBox,
  alarmMetaData,
  handleCancel,
  alarmMetaDataState
}) => {
  const frequencyMetaDataState = useSelector((state: RootState) => state.frequencyMetaDataState)

  const executionFrequencyOptions = React.useMemo(() => {
    if (frequencyMetaDataState.data?.kpiFrequencies != null && frequencyMetaDataState.data.kpiFrequencies.length > 0) {
      return convertMinutesToDisplayValueOption(frequencyMetaDataState.data.kpiFrequencies)
    }
    // Fallback to static options if API data is not available
    return [
      { label: 'Default (1 Minute)', value: 'Default (1 Minute)' },
      { label: '3 Minutes', value: '3 Minutes' },
      { label: '5 Minutes', value: '5 Minutes' }
    ]
  }, [frequencyMetaDataState.data?.kpiFrequencies])

  React.useEffect(() => {
    alarmMetaData()
  }, [])

  const getTestIdPrefix = (): string => {
    return instanceType === AddDeviceStrings.standard
      ? 'std'
      : AddDeviceStrings.custom
  }

  return (
    <TableContainer className='indicator-table' sx={styles.tableContainer}>
      <Table sx={styles.table} stickyHeader>
        <TableHead sx={styles.tableHead} >
          <TableRow sx={{ zIndex: 3, position: 'sticky' }}>
            <TableCell sx={styles.tableHeadCell}>
              <Checkbox
                sx={styles.selectAllCheckbox}
                data-testid={`${getTestIdPrefix()}-select-all-checkbox`}
                onChange={toggleSelectAll}
                indeterminate={
                  selectedCount > 0 && selectedCount < indicators.length
                }
                checked={
                  indicators.length > 0 && selectedCount === indicators.length
                }
                inputProps={{
                  'aria-label': 'Select all'
                }}
              />
            </TableCell>
            {tableHeaders.map((header) => (
              <TableCell key={header} sx={styles.tableHeadCell}>
                <Typography sx={styles.tableHeaderTypography}>
                  {header}
                </Typography>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        {indicators.length === 0
          ? (
          <EmptyTable />
            )
          : (
          <TableBody className='indicator-table-body'>
            {indicators.map((indicator, index) => (
              <TableRow key={indicator.kpiDefinitionId}>
                <TableCell sx={styles.tableCell}>
                  <Checkbox
                    color='primary'
                    data-testid={`${getTestIdPrefix()}-select-indicator-${
                      indicator.kpiDefinitionId
                    }`}
                    sx={styles.checkbox}
                    checked={indicator.isKpiEnabled}
                    onChange={() => {
                      toggleSelectIndicator(indicator.kpiDefinitionId)
                    }}
                  />
                </TableCell>
                <TableCell sx={{ ...styles.tableCell, ...{ minWidth: '250px', width: { sm: '30%', md: '30%', lg: '350px' } } }}>
                  <Typography sx={styles.tableCellTypography}>
                    {indicator.kpiName}
                  </Typography>
                </TableCell>
                <TableCell sx={{ ...styles.tableCell, minWidth: '195px', padding: 1.2 }}>
                      <GenXDropdown
                        menuPlacement={(index > indicators.length - 3 && indicators.length > 2) ? 'top' : 'auto'}
                        options={executionFrequencyOptions}
                        value={findExecutionFrequencyOption(indicator.executionFrequencyMinutes, indicator.isTotalizer, executionFrequencyOptions)}
                        onSelect={(selectedOption: any) => {
                          const minutes = convertDisplayValueToMinutes(selectedOption.value)
                          handleChangeTextBox(
                            indicator.kpiDefinitionId,
                            'executionFrequencyMinutes',
                            minutes
                          )
                        }}
                        placeholder="Select Frequency"
                        width={198}
                        height={150}
                        disabled={!indicator.isKpiEnabled || Boolean(indicator.isTotalizer) || frequencyMetaDataState.isLoading}
                        inputId={`pooling-interval-${indicator.kpiDefinitionId}`}
                        isSearchable={false}
                      />
                </TableCell>
                <TableCell sx={{ ...styles.tableCell, minWidth: '160px' }}>
                  <ConfigureDevice
                    isEdit={false}
                    isAlarmEnabledCompo={!indicator.isKpiEnabled}
                    deviceIndicators={indicator}
                    handleChangeTextBox={handleChangeTextBox}
                    alarmSelect={handleAlarmSelect}
                    getTestIdPrefix={getTestIdPrefix}
                    handleCancel={handleCancel}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
            )}
      </Table>
    </TableContainer>
  )
}

interface DispatchToProps {
  alarmMetaData: () => void
}
const mapDispatchToProps = (dispatch: any): DispatchToProps => ({
  alarmMetaData: () => dispatch(alarmMetaData())
})
interface StateToProps {
  alarmMetaDataState: AlarmMetaDataState
}
const mapStateToProps = (state: RootState): StateToProps => ({
  alarmMetaDataState: state.alarmMetaDataState
})

const connector = connect(mapStateToProps, mapDispatchToProps)
type PropsFromRedux = ConnectedProps<typeof connector>

export default connector(PerformanceIndicatorsTable)
